import {ChangeDetectorRef, Component, computed, DoCheck, HostListener, OnDestroy, OnInit, signal,} from '@angular/core';
import lottie, {AnimationItem} from 'lottie-web';
import {toObservable} from '@angular/core/rxjs-interop';
import {MatDialog} from '@angular/material/dialog';
import {AboutAppDialogComponent} from '../dialogs/about-app-dialog.component';
import {CytoscapeService} from '../services/cy-services/cytoscape.service';
import {NodeService} from '../api/services/node.service';
import {MatBottomSheet} from '@angular/material/bottom-sheet';
import {MapListBottomsheetComponent} from '../dialogs/map-list.bottomsheet.component';
import {GraphMap, GraphNode, GraphNodeLink, readSelectedMap,} from '../api/interfaces/graph-node.interface';
import {MapService} from '../api/services/map.service';
import {UserService} from '../api/services/user.service';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {LinkViewMode, SettingsService} from '../api/services/setting.service';
import {SettingsDialogComponent} from '../dialogs/settings-dialog.component';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  finalize,
  forkJoin,
  map,
  of,
  Subject,
  Subscription,
  takeUntil,
  throwError,
} from 'rxjs';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ShareMapDialogComponent} from '../dialogs/share-map.dialog';
import {User} from '../api/interfaces/user.interface';
import {ViewService} from '../api/services/view.service';
import {ViewsListBottomsheetComponent} from '../dialogs/views-list.bottomsheet.component';
import {TourListBottomsheetComponent} from '../dialogs/tour-list.bottomsheet.component';
import {ToursService} from '../api/services/tour.service';
import {
  FilterDto,
  MapStateDto,
  ModelDto,
  ModelElementType,
  MODULE_NAMES,
  OtherLinksMode,
  OtherNodesMode,
  PACKAGE_NAMES,
} from '@maporium-workspace/shared';
import {PackageService} from '../api/services/package.service';
import {ReleaseNotesDialogComponent} from '../dialogs/release-notes-dialog.componet';
import {environment} from '../../environments/environment';
import {BlockUIService} from 'ng-block-ui';
import {CreateNewTourDialogComponent} from '../dialogs/create-new-tour-dialog.component';
import {CreateNewViewDialogComponent} from '../dialogs/create-new-view-dialog.component';
import {SavedView} from '../api/interfaces/saved-view';
import {MapTour} from '../api/interfaces/map-tour';
import {NotificationService} from '../services/notification.service';
import {CreateNewMapDialogComponent} from '../dialogs/create-new-map-dialog.component';
import {MapDialogsService} from '../dialogs/map-dialogs.service';
import {DEFAULT_MAP_STATE, MapStateService,} from '../api/services/map-state.service';
import {CreateNewMapStateDialogComponent} from '../dialogs/create-new-map-state-dialog.component';
import {StateListBottomsheetComponent} from '../dialogs/state-list.bottomsheet.component';
import {
  deletePanZoomState,
  invalidateGeneralPanZoom,
  invalidatePanZoomForCollapsedNode,
} from '../../cytoscape-extensions/pan-zoom-storage';
import {isInIframe} from '../shared/helpers/maporium.validators';
import {AppWideStateService} from '../services/app-state/app-wide-state.service';
import {EmbedDialogComponent} from '../dialogs/embed-dialog.component';
import {ChatBottomsheetComponent} from '../shared/components/ai-chat-dialog/ai-chat-dialog.component';
import {ImportDialogData, ImportMapDialogComponent,} from '../dialogs/import-map.dialog.component';
import {ImportSmapDialogComponent} from '../dialogs/import-smap-dialog.component';
import {GenerateSmapPromptDialogComponent} from '../dialogs/generate-smap-prompt.dialog.component';
import {AiOptimizerDialogComponent, AiOptimizerDialogData} from '../dialogs/ai-optimizer-dialog.component';
import {first, get, isEmpty, isUndefined} from 'lodash';
import {TransferMapDialogComponent} from '../dialogs/transfer-map.dialog';
import {MapRoleService} from '../acl/map-role.service';
import {AiStateService} from '../services/ai-state.service';
import {GenerationStateService} from '../services/generation-state.service';
import {Permission} from '../acl/map-user-roles.enum';
import {UserAccountDialogComponent} from '../dialogs/user-account-dialog.component';
import {AiUsageDialogComponent} from '../dialogs/ai-usage-dialog.component';
import {UserProfileDialogComponent} from '../dialogs/user-profile-dialog.component';
import {TutorialBrowserBottomsheetComponent} from '../dialogs/tutorial-browser.bottomsheet.component';
import {MarkdownRulesDialogComponent} from '../dialogs/markdown-rules-dialog.component';
import {FilterService} from '../api/services/filter.service';
import {CreateFilterDialogComponent} from '../dialogs/create-filter-dialog.component';
import {FilterListBottomsheetComponent} from '../dialogs/filter-list.bottomsheet.component';
import {StoreService} from '../api/services/store.service';
import {ACCESS_TOKEN_STORAGE_KEY} from '../shared/storage-keys';
import {PublishToStoreDialogComponent} from '../dialogs/publish-to-store.dialog';
import {StoreBrowserBottomsheetComponent} from '../dialogs/store-browser.bottomsheet.component';
import {ModelService} from '../api/services/model.service';
import {LegendService} from '../api/services/legend.service';
import {CreateModelDialogComponent} from '../dialogs/create-model-dialog.component';
import {ModelListBottomsheetComponent} from '../dialogs/model-list.bottomsheet.component';
import {ConfirmationDialogComponent} from '../dialogs/confirmation-dialog.component';
import {
  AiBubbleStateService,
  AIGenerationBubbleSource,
} from '../shared/components/ai-generation-bubble/ai-bubble-state.service';
import {TopToolbarStateService} from './top-toolbar-state.service';
import {MapScreenshotService} from '../services/map-screenshot.service';
import {OverlayElementManagerService} from '../services/overlay-element-manager.service';
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';
import {buildLinkShapeSvg, buildNodeShapeSvg} from '../shared/helpers/legend-shape.helper';
import {OnboardingService} from '../services/onboarding.service';
import {TranslateService} from '@ngx-translate/core';
import {SKIP_MODEL_APPLICATION_CONFIRMATION_KEY} from './dismissible-prefs';

interface MapWithOriginalName extends GraphMap {
  originalName?: string;
}

@Component({
  selector: 'maporium-toolbar',
  styleUrls: ['./top-toolbar.component.scss'],
  templateUrl: './top-toolbar.component.html',
})
export class TopToolbarComponent implements OnInit, OnDestroy, DoCheck {
  private _currentMapSignal = signal<MapWithOriginalName | undefined>(undefined);

  public get currentMap(): MapWithOriginalName | undefined {
    return this._currentMapSignal();
  }

  public set currentMap(map: MapWithOriginalName | undefined) {
    if (map?.id) this.seenMapIds.add(map.id);
    this._currentMapSignal.set(map);
  }

  private readonly seenMapIds = new Set<string>();

  public get unseenMaps(): GraphMap[] {
    return this.maps.filter((map) => map?.isUnseen && !this.seenMapIds.has(map.id));
  }

  /** Cached permission flags — only recompute when currentMap signal changes */
  canEdit = computed(() => {
    const map = this._currentMapSignal();
    if (!map || isInIframe()) return false;
    return this.mapRoleService.hasCurrentUserPermission('edit', map);
  });
  /** Viewers and commenters get no assistant, on any smap. */
  canUseAssistant = computed(() => this.mapRoleService.canUseAssistant(this._currentMapSignal()));
  canManage = computed(() => this.mapRoleService.canManage(this._currentMapSignal()));

  public currentUser: User | undefined;
  public maps: GraphMap[] = [];
  public showGptSave = false;
  public isEditingMapName = false;
  public mapEditForm = new FormGroup({
    name: new FormControl('', Validators.required),
  });
  public showAiBubble = false;
  public aiBubbleConfig: any = {
    type: 'configure',
  };

  readonly MODULE_NAMES = MODULE_NAMES;

  public isLoggedIn = false;
  private isLoading = false;
  private isLoadingToursAndViews = false;
  public buildNumber = {
    build: '',
    version: '',
  };
  public isSidebarOpen = false;
  showVersionInfo = false;
  public servicePlan = '';
  tours: any[] = [];
  views: SavedView[] = [];
  filters: FilterDto[] = [];
  models: ModelDto[] = [];
  nodeModels: ModelDto[] = [];
  linkModels: ModelDto[] = [];

  // Cache for legend-shaped icons in the models menu. Icons must be cached
  // (stable reference) so Angular's change detection doesn't reapply innerHTML
  // every tick and trip a CD loop.
  private modelIconCache = new Map<string, SafeHtml>();
  mapStates: MapStateDto[] = [];
  defaultState = DEFAULT_MAP_STATE;
  packagenames = PACKAGE_NAMES;
  aiBubbleSources = AIGenerationBubbleSource;
  isIframe = isInIframe();
  readModeAppState = false;
  hideToolbar = false;
  private toolbarHiddenRoutes = ['/payment', '/close-account', '/inactive'];
  featuredState: MapStateDto | null = null;
  isRectangleFeatureEnabled = !!localStorage.getItem('ENABLE_RECTANGLE_FEATURE');
  protected readonly isInIframe = isInIframe;

  private destroy$ = new Subject<void>();
  private hasInitializedDashboard = false;
  private hasInitializedAll = false;
  private initAllSubscriptions = new Subscription();

  // Convert readMode signal to observable in injection context
  private readMode$ = toObservable(this.appWideStateService.readMode);
  /** In a field initializer: toObservable() needs an injection context. */
  private readonly currentMapId$ = toObservable(this.mapService.selectedMap).pipe(
    map((m) => m?.id),
    distinctUntilChanged()
  );

  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    private matBottomSheet: MatBottomSheet,
    private mapService: MapService,
    private settingsService: SettingsService,
    private tourService: ToursService,
    private viewService: ViewService,
    private blockUiService: BlockUIService,
    private cdr: ChangeDetectorRef,
    private userService: UserService,
    private router: Router,
    private packageService: PackageService,
    private toursService: ToursService,
    private notificationService: NotificationService,
    private route: ActivatedRoute,
    private mapDialogsService: MapDialogsService,
    private mapStateService: MapStateService,
    private mapRoleService: MapRoleService,
    private appWideStateService: AppWideStateService,
    private filterService: FilterService,
    private modelService: ModelService,
    private dialog: MatDialog,
    public aiStateService: AiStateService,
    public topToolbarStateService: TopToolbarStateService,
    private aiBubblesStateService: AiBubbleStateService,
    private storeService: StoreService,
    private screenshotService: MapScreenshotService,
    private overlayManager: OverlayElementManagerService,
    private sanitizer: DomSanitizer,
    private legendService: LegendService,
    private onboardingService: OnboardingService,
    private generationStateService: GenerationStateService,
    private translate: TranslateService,
  ) {}

  private static readonly SMAP_LOTTIE_PATH = 'assets/lottie/symbol-smap.json';
  private static readonly ASSISTANT_LOTTIE_PATH = 'assets/lottie/symbol-assistant.json';
  public isSmapGenerating = false;
  public isAssistantAnimating = false;
  public isPropertyGenerating = false;
  private smapLottieInstance: AnimationItem | null = null;
  private assistantLottieInstance: AnimationItem | null = null;

  private applyLegendOrdering(): void {
    const compare = (a: ModelDto, b: ModelDto) => a.name.localeCompare(b.name);
    this.nodeModels = [...this.nodeModels].sort(compare);
    this.linkModels = [...this.linkModels].sort(compare);
    this.models = [...this.nodeModels, ...this.linkModels];
  }

  /**
   * Precompute legend-style SVG icons for each model keyed by id. Called
   * whenever `nodeModels` / `linkModels` are replaced so that `modelIcon()`
   * returns a stable `SafeHtml` reference per model — a fresh reference on
   * every CD tick drives Angular into an infinite change-detection loop.
   */
  private rebuildModelIconCache(): void {
    this.modelIconCache.clear();
    for (const m of this.nodeModels) {
      const svg = buildNodeShapeSvg(m.elementDefinition?.originalElement, {size: 14, strokeWidth: 2});
      this.modelIconCache.set(m.id, this.sanitizer.bypassSecurityTrustHtml(svg));
    }
    for (const m of this.linkModels) {
      const svg = buildLinkShapeSvg(m.elementDefinition?.originalElement, {size: 16, strokeWidth: 2});
      this.modelIconCache.set(m.id, this.sanitizer.bypassSecurityTrustHtml(svg));
    }
  }

  modelIcon(modelId: string): SafeHtml | undefined {
    return this.modelIconCache.get(modelId);
  }


  ngDoCheck(): void {
    this.isBlocking = this.blockUiService.isActive('UI');
  }

  ngOnInit(): void {
    // Initialize tooltip fix ONCE (not on every map change)
    setTimeout(() => {
      this.fixContextMenuTooltips();
    }, 1000);

    // Load user's store library for icon detection (only if authenticated)
    if (localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)) {
      this.storeService.getUserLibrary().subscribe();
    }

    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        debounceTime(100), // Wait for rapid redirects to settle
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        // Hide toolbar on non-app routes
        this.hideToolbar = this.toolbarHiddenRoutes.some(r => this.router.url.startsWith(r));

        // Check if we are on dashboard
        if (this.router.url.includes('/dashboard')) {
          // Only load once on first dashboard navigation
          if (!this.hasInitializedDashboard) {
            this.hasInitializedDashboard = true;
            this.handleMapsLoad();
            this.loadMapStates();
            if (!this.isIframe) {
              this.loadToursAndViews();
            }
          } else {
            // On subsequent navigations, update currentMap from route data or fetch by ID
            // Extract map ID from URL - more reliable than ActivatedRoute snapshot for components outside router-outlet
            const mapIdMatch = this.router.url.match(/\/dashboard\/map\/([^/?]+)/);
            const mapId = mapIdMatch ? mapIdMatch[1] : null;
            if (mapId && this.currentMap?.id !== mapId) {
              // Find the map in our maps list
              const map = this.maps.find(m => m.id === mapId);
              if (map) {
                this.currentMap = map;
                this.topToolbarStateService.setMapName(this.currentMap.name);
                this.mapEditForm.setValue({name: this.currentMap.name});
                this.loadMapStates();
                this.isLoadingToursAndViews = false;
                if (!this.isIframe) {
                  this.loadToursAndViews();
                }
                this.cdr.markForCheck();
              } else {
                // Map not in local list (e.g., newly created from template), fetch from server
                this.mapService.findByIdWithRelation(mapId).subscribe({
                  next: (fetchedMap) => {
                    this.currentMap = fetchedMap;
                    this.maps.push(fetchedMap); // Add to local list
                    this.topToolbarStateService.setMapName(fetchedMap.name);
                    this.mapEditForm.setValue({name: fetchedMap.name});
                    this.loadMapStates();
                    this.isLoadingToursAndViews = false;
                    if (!this.isIframe) {
                      this.loadToursAndViews();
                    }
                    this.cdr.markForCheck();
                  },
                  error: (err) => {
                    console.warn('Failed to fetch map:', err);
                  }
                });
              }
            }
          }
        } else if (
          this.router.url === '/signin' ||
          this.router.url === '/close-account' ||
          this.router.url.includes('inactive')
        ) {
          this.isLoggedIn = false;
          this.hasInitializedDashboard = false; // Reset for next dashboard visit
        }
      });

    if (!isInIframe()) {
      this.packageService.userPackageStream()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => {
          this.cdr.markForCheck();
        });
    }

    // Subscribe to readMode state changes
    this.readMode$
      .pipe(takeUntil(this.destroy$))
      .subscribe((readMode: boolean) => {
        this.readModeAppState = readMode;
      });

    this.generationStateService.smapGenerationInProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((inProgress) => {
        this.isSmapGenerating = inProgress;
        this.cdr.markForCheck();
        if (inProgress) {
          setTimeout(() => this.initSmapLottie(), 0);
        } else {
          this.destroySmapLottie();
        }
      });

    // AC 5.a — suppress the toolbar's mat-progress-bar while a property
    // generation is running. The animated generate icon is the feedback,
    // the underline bar would be redundant noise during that specific flow.
    this.generationStateService.propertyGenerationInProgress$
      .pipe(takeUntil(this.destroy$))
      .subscribe((inProgress) => {
        this.isPropertyGenerating = inProgress;
        this.cdr.markForCheck();
      });

    // MAP-1402 / MAP-1618: with the assistant closed the toolbar stands in for
    // every running request. With it open, the assistant already shows this
    // smap's request, so the toolbar reports only the ones belonging to other
    // smaps, which means both can animate at the same time.
    this.generationStateService.toolbarAssistantAnimating$(this.currentMapId$)
      .pipe(takeUntil(this.destroy$))
      .subscribe((animating) => {
        this.isAssistantAnimating = animating;
        this.cdr.markForCheck();
        if (animating) {
          setTimeout(() => this.initAssistantLottie(), 0);
        } else {
          this.destroyAssistantLottie();
        }
      });

    // Subscribe to model changes to refresh model lists when models are edited
    this.modelService.modelsChangedSubject
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        // Use a try-catch and individual loading to prevent complete failure
        try {
          this.loadToursAndViews();
        } catch (error) {
          console.warn('Error in model change subscription:', error);
          // Fallback to individual service loading
          const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
          if (mapId) {
            this.loadIndividualServices(mapId, true);
          }
        }
      });
  }

  @HostListener('document:click', ['$event'])
  handleDocumentClick(event: MouseEvent) {
    if (this.isEditingMapName) {
      const target = event.target as HTMLElement;
      if (
        !target.className.includes('map-name-container') &&
        !target.className.includes('app-text-input')
      ) {
        this.stopMapEdit();
      }
    }

    if (this.showAiBubble) {
      const target = event.target as HTMLElement;
      const bubble = document.querySelector('app-ai-generation-bubble');
      const aiButton = document
        .querySelector('.icon-smap-function-assistant')
        ?.closest('button');

      if (bubble && !bubble.contains(target) && !aiButton?.contains(target)) {
        this.onAiBubbleClose();
      }
    }
  }

  hasMapRole(role: Permission) {
    if (isInIframe()) {
      return role === 'view';
    }
    if (!this.currentMap) return false;
    return this.mapRoleService.hasCurrentUserPermission(role, this.currentMap);
  }

  getMapAccessIcon(map?: GraphMap) {
    const currentMap = map || this.currentMap;
    const isOwner = currentMap?.owner?.id === this.currentUser?.id;

    // Check if map was added from store library first (non-owners only, and not shared with user)
    // Sharing takes precedence over library - shared users should see role-based icons
    if (currentMap && !isOwner && this.isInLibrary(currentMap) && !this.isSharedWithMe(currentMap)) {
      // Use template icon if it's an authorized template, otherwise viewer icon
      if (currentMap.isTemplate && currentMap.allowViewersToCreateFromTemplate) {
        return 'icon-smap-object-template';
      }
      return 'icon-smap-role-viewer';
    }

    const role =
      currentMap?.userRoles?.find((r) => r.user.id === this.currentUser?.id)
        ?.role || 'viewer';
    if (currentMap?.isReadonly) {
      switch (role) {
        case 'manager':
          return 'icon-smap-role-manager';
        case 'editor':
          return 'icon-smap-role-editor';
        case 'commenter':
          return 'icon-smap-role-commenter';
        default:
          return 'icon-smap-role-viewer';
      }
    } else {
      return (currentMap?.sharedWith?.length || 0) > 0
        ? 'icon-smap-role-owner-shared'
        : 'icon-smap-role-owner';
    }
  }

  onMapNameKeydown(event: KeyboardEvent, val: any) {
    //Timeless Timeout is required because keyEvent has to propagate first
    setTimeout(() => {
      const newMapName = val.actualInput.nativeElement.value;
      if (
        newMapName === this.currentMap?.name &&
        (event.key === 'Return' || event.key === 'Enter')
      ) {
        this.isEditingMapName = false;
        //@ts-ignore
        event?.target?.blur();
        return;
      }

      if (this.mapEditForm.invalid) return;

      if (event.key === 'Enter' || event.key === 'Return') {
        this.stopMapEdit(event);
      }
      this.topToolbarStateService.setMapName(newMapName);
      this.updateMapName(newMapName);
    });
  }

  // Method to handle the actual update to the server
  private updateMapName(name: string | null): void {
    if (isInIframe()) return;
    if (!this.currentMap || this.isLoading) return;

    // Get the latest map data from localStorage to preserve any recent description changes
    const freshMap = this.mapService.getCurrentSelectedMapFromStore();

    const payload = {
      ...this.currentMap,
      name: name,
      // Preserve the description from localStorage in case sidebar updated it recently
      description: freshMap?.description || this.currentMap.description,
    };

    // Remove the temporary property before sending to server
    delete payload.originalName;

    this.isLoading = true;
    this.mapService
      .update(payload as any)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe();
  }

  isBlocking = false;

  // The canvas's own large loading animation is already showing feedback for
  // this exact HTTP activity; the toolbar's thin progress bar underneath it
  // would just be redundant, choppy-looking noise on top of it.
  get isMapLoading(): boolean {
    return !this.appWideStateService.canvasLoadedSignal();
  }

  isInLibrary(map: GraphMap): boolean {
    return this.storeService.isMapInUserLibrary(map.id);
  }

  /**
   * Check if the smap is explicitly shared with the current user (via sharedWith).
   * Sharing takes precedence over library addition - users with shared access
   * should see "Remove" (person_remove) not "Remove from Collection".
   */
  isSharedWithMe(map: GraphMap): boolean {
    const currentUserId = this.currentUser?.id;
    if (!currentUserId || !map.sharedWith) return false;
    return map.sharedWith.some(user => user.id === currentUserId);
  }

  get isDefaulter(): boolean {
    return this.packageService.isDefaulter();
  }

  public shouldShowModelsMenu(): boolean {
    return !(this.isDefaulter && this.models.length === 0);
  }

  get isOwner() {
    return this.currentMap?.owner?.id === this.currentUser?.id;
  }

  get canManageViews() {
    return this.packageService.hasModule(MODULE_NAMES.VIEW_MANAGEMENT);
  }

  get canManageTours() {
    return this.packageService.hasModule(MODULE_NAMES.TOUR_MANAGEMENT);
  }

  get canManageFilters() {
    return (
      this.packageService.hasPackage(PACKAGE_NAMES.publisher) ||
      this.packageService.hasPackage(PACKAGE_NAMES.analyst) ||
      this.packageService.hasPackage(PACKAGE_NAMES.developer)
    );
  }

  public loadToursAndViews(emitMenuChange = true) {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) {
      return;
    }

    // Logged-out tab still has a stale selectedMap in storage; firing these
    // would 401 and trigger the refresh-token cascade that races sign-in.
    if (!isInIframe() && !localStorage.getItem(ACCESS_TOKEN_STORAGE_KEY)) {
      return;
    }

    if (this.isLoadingToursAndViews && !isInIframe()) return;

    this.isLoadingToursAndViews = true;
    const publicOrPrivateTours = isInIframe()
      ? this.toursService.getAllPublic(mapId)
      : this.toursService.getAll(mapId);
    const publicOrPrivateViews = isInIframe()
      ? this.viewService.getAllPublic(mapId)
      : this.viewService.getAll(mapId);
    const filters = isInIframe()
      ? this.filterService.getAllGlobal()
      : this.filterService.getAll(mapId);
    const models = isInIframe()
      ? of({ nodeModels: [], linkModels: [] })
      : this.modelService.getAll(mapId);
    forkJoin([
      publicOrPrivateTours,
      publicOrPrivateViews,
      filters,
      models,
    ]).subscribe({
      next: ([tours, views, filters, modelsResponse]) => {
        this.tours = tours;
        this.views = views;
        this.filters = filters;
        this.nodeModels = modelsResponse.nodeModels.sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        this.linkModels = modelsResponse.linkModels.sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        this.models = [...this.nodeModels, ...this.linkModels];
        this.applyLegendOrdering();
        this.rebuildModelIconCache();
        if (emitMenuChange) {
          CytoscapeService.menuChangedSubject.next();
        }
      },
      error: (err) => {
        this.isLoadingToursAndViews = false;
        console.warn('Error loading toolbar data:', err);

        // Only hide toolbar for auth errors, preserve existing data for other errors
        if (err.status === 401) {
          this.isLoggedIn = false;
        } else {
          // For non-auth errors, try to load individual services that might still work
          this.loadIndividualServices(mapId, emitMenuChange);
        }
      },
      complete: () => (this.isLoadingToursAndViews = false),
    });
  }

  private loadIndividualServices(mapId: string, emitMenuChange: boolean) {
    // Try to load each service individually, preserving existing data on failure
    const publicOrPrivateTours = isInIframe()
      ? this.toursService.getAllPublic(mapId)
      : this.toursService.getAll(mapId);
    const publicOrPrivateViews = isInIframe()
      ? this.viewService.getAllPublic(mapId)
      : this.viewService.getAll(mapId);
    const filters = isInIframe()
      ? this.filterService.getAllGlobal()
      : this.filterService.getAll(mapId);
    const models = isInIframe()
      ? of({ nodeModels: [], linkModels: [] })
      : this.modelService.getAll(mapId);

    // Load tours
    publicOrPrivateTours.subscribe({
      next: (tours) => {
        this.tours = tours;
      },
      error: (err) => {
        console.warn('Failed to load tours:', err);
      },
    });

    // Load views
    publicOrPrivateViews.subscribe({
      next: (views) => {
        this.views = views;
      },
      error: (err) => {
        console.warn('Failed to load views:', err);
      },
    });

    // Load filters
    filters.subscribe({
      next: (filters) => {
        this.filters = filters;
      },
      error: (err) => {
        console.warn('Failed to load filters:', err);
      },
    });

    // Load models
    models.subscribe({
      next: (modelsResponse) => {
        this.nodeModels = modelsResponse.nodeModels.sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        this.linkModels = modelsResponse.linkModels.sort((a, b) =>
          a.name.localeCompare(b.name),
        );
        this.models = [...this.nodeModels, ...this.linkModels];
        this.applyLegendOrdering();
        this.rebuildModelIconCache();
        if (emitMenuChange) {
          CytoscapeService.menuChangedSubject.next();
        }
      },
      error: (err) => {
        console.warn('Failed to load models:', err);
      },
    });
  }

  openReleaseNotes() {
    this.dialog.open(ReleaseNotesDialogComponent, {
      width: '35%',
    });
  }

  openVideoTutorialsDialog() {
    this.matBottomSheet.open(TutorialBrowserBottomsheetComponent, {
      panelClass: 'tutorial-browser-bottom-sheet',
    });
  }

  runOnboardingGuide() {
    this.onboardingService.run();
  }

  openMarkdownRulesDialog() {
    this.dialog.open(MarkdownRulesDialogComponent, {
      width: '35%',
    });
  }

  stopMapEdit(event?: any) {
    if (this.mapEditForm.invalid) return;
    this.isEditingMapName = false;

    // When editing stops, sync currentMap with the stored version from server
    const storedMap = this.mapService.getCurrentSelectedMapFromStore();
    if (storedMap) {
      this.currentMap = storedMap;
      // Clear the original name tracking since we're done editing
      delete this.currentMap.originalName;
    }

    if (event) {
      event.target.blur();
    }
  }

  createTour() {
    if (!this.hasMapRole('edit')) {
      return;
    }
    const ref = this.dialog.open(CreateNewTourDialogComponent);
    ref.afterClosed().subscribe((tour: any) => {
      if (tour) {
        const mapId = this.mapService.getCurrentSelectedMapFromStore().id;
        if (!mapId) {
          return;
        }
        (isInIframe()
          ? this.toursService.getAllPublic(mapId)
          : this.toursService.getAll(mapId)
        ).subscribe();
        CytoscapeService.menuChangedSubject.next();
      }
    });
  }

  createView() {
    if (!this.hasMapRole('edit')) {
      return;
    }
    const ref = this.dialog.open(CreateNewViewDialogComponent, {
      width: '35%',
      autoFocus: 'input',
    });
    ref.afterClosed().subscribe((view: SavedView) => {
      if (view) {
        CytoscapeService.menuChangedSubject.next();
      }
    });
  }

  private leaveDraft(): Promise<boolean> {
    if (!this.router.url.startsWith('/dashboard/draft/')) return Promise.resolve(true);
    return this.router.navigate(['/dashboard'], {replaceUrl: true});
  }

  async createNewMap() {
    if (!(await this.leaveDraft())) return;
    const ref = this.dialog.open(CreateNewMapDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      autoFocus: 'input',
    });
    ref.afterClosed().subscribe((map: GraphMap) => {
      if (map) {
        this.tourService.activateTourSubject.next(null);

        // Use new endpoint that creates map with default node on the backend
        // This eliminates race conditions between map creation and node creation
        if (get(map, 'isFromTemplate')) {
          // For template maps, use the old flow
          const newMap = { ...map, isNew: true };
          this.selectMap(newMap, true);
        } else {
          // For new maps, use the new create-default endpoint
          this.mapService
            .createDefaultMap(map)
            .subscribe((createdMap: GraphMap) => {
              const newMap = { ...createdMap, isNew: true };
              this.selectMap(newMap);
            });
        }
      }
    });
  }

  async startTour(tour: MapTour) {
    if (tour.steps.length === 0) {
      this.notificationService.error(this.translate.instant('NAV_MISC.TOUR_NO_STEPS'));
    } else {
      await this.tourService.playTour(tour);
    }
  }

  async startView(view: SavedView) {
    await this.selectView(view);
  }

  handleGpt(addTo: 'map' | 'new') {
    this.nodeService.addGptToSubject.next(addTo);
  }

  toggleEdges(type: LinkViewMode) {
    this.settingsService.setLinkViewMode(type);
    this.settingsService.linkViewModeSignal.set(type);
  }

  get currentEdgeView() {
    return this.settingsService.linkViewMode;
  }

  setOtherLinksMode(mode: OtherLinksMode) {
    this.settingsService.setOtherLinksMode(mode);
  }

  get otherLinksMode() {
    return this.settingsService.otherLinksMode;
  }

  setOtherNodesMode(mode: OtherNodesMode) {
    this.settingsService.setOtherNodesMode(mode);
  }

  get otherNodesMode() {
    return this.settingsService.otherNodesMode;
  }

  protected readonly otherLinksModes = OtherLinksMode;
  protected readonly otherNodesModes = OtherNodesMode;

  openAboutDialog(): void {
    this.dialog.open(AboutAppDialogComponent, {
      width: '400px',
    });
  }

  public handleMapsLoad() {
    // Extract map ID from URL path (for /dashboard/map/{id}) or query params (for iframe ?mapId=)
    const mapIdFromPath = this.router.url.match(/\/dashboard\/map\/([^/?]+)/)?.[1] || null;
    const mapId = this.route.snapshot.queryParamMap.get('mapId') || mapIdFromPath;
    const sharedMapId = localStorage.getItem('sharedMapId');
    if (this.readModeAppState) {
      this.isLoading = true;
      if (!mapId) {
        this.router.navigate(['/404']);
        this.isLoading = false;
        return;
      }
      this.mapService
        .findPublicMap(mapId as string)
        .pipe(
          catchError((err) => {
            this.isLoading = false;
            this.router.navigate(['/404']);
            return throwError(err);
          }),
          finalize(() => (this.isLoading = false)),
        )
        .subscribe((map) => {
          this.currentMap = map;
          this.topToolbarStateService.setMapName(this.currentMap.name);
          this.mapService.mapChangedSubject.next(map);
          this.mapService.setCurrentSelectedMapToStore(map);
          this.initAll();
          this.mapEditForm.setValue({ name: map.name });
          this.router
            .navigate([], {
              relativeTo: this.route,
              queryParamsHandling: isInIframe() ? 'preserve' : undefined,
            })
            .then(() => {
              this.mapEditForm.setValue({ name: map.name });
            });
        });
    } else {
      this.loadMapForUser(mapId as string, sharedMapId as string);
    }
  }

  public shouldShowStatesMenu() {
    const isExplorer = this.packageService.hasPackage(
      this.packagenames.explorer,
    );
    const isCreator = this.packageService.hasPackage(this.packagenames.creator);
    const isDefaulter = this.packageService.isDefaulter();

    return !((isExplorer || isCreator || isDefaulter) && this.mapStates.length === 0);
  }

  public shouldShowScenesMenu() {
    const isExplorer = this.packageService.hasPackage(
      this.packagenames.explorer,
    );

    if (isInIframe() && (!this.views?.length || isUndefined(this.views))) {
      return false;
    }

    return !((isExplorer || this.isDefaulter) && this.views.length === 0);
  }

  public shouldShowStoriesMenu() {
    const isExplorer = this.packageService.hasPackage(
      this.packagenames.explorer,
    );
    if (isInIframe() && this.tours.length === 0) {
      return false;
    }
    return !((isExplorer || this.isDefaulter) && this.tours.length === 0);
  }

  public shouldShowView(view: SavedView): boolean {
    if (!view.excludeFromReadOnly) return true;
    return this.currentMap?.owner?.id === this.currentUser?.id || this.hasMapRole('edit');
  }
  reloadMapList() {
    this.isLoading = true;
    this.mapService
      .findAllForUser(this.userService.getCurrentUserFromStorage().id)
      .pipe(
        catchError((err) => {
          this.isLoading = false;
          return throwError(err);
        }),
      )
      .subscribe((maps) => {
        this.isLoading = false;
        // @ts-ignore
        this.maps = maps;
      });
  }

  loadModelsForMenu() {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) {
      return;
    }

    this.modelService.getAll(mapId).subscribe((modelsResponse) => {
      this.nodeModels = modelsResponse.nodeModels.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      this.linkModels = modelsResponse.linkModels.sort((a, b) =>
        a.name.localeCompare(b.name),
      );
      this.models = [...this.nodeModels, ...this.linkModels];
      this.applyLegendOrdering();
      this.rebuildModelIconCache();
    });
  }

  loadFiltersForMenu() {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;
    if (!mapId) {
      return;
    }

    const filters = isInIframe()
      ? this.filterService.getAllGlobal()
      : this.filterService.getAll(mapId);
    filters.subscribe((filters) => {
      this.filters = filters;
    });
  }

  openStateMenu() {
    // Skip automatic featured-state selection when opening menu
    this.loadMapStates(false);
  }

  loadMapStates(autoSelect = true, retryCount = 3, delayMs = 1000): void {
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;

    if (!mapId) {
      if (retryCount > 0) {
        // preserve autoSelect flag when retrying
        setTimeout(
          () => this.loadMapStates(autoSelect, retryCount - 1, delayMs),
          delayMs,
        );
      } else {
        console.warn('Unable to fetch mapId after retries.');
        return;
      }
      return;
    }

    const statesStream = this.isIframe
      ? this.mapStateService.findAllForMapPublic(mapId)
      : this.mapStateService.findAllForMap(mapId);
    statesStream.subscribe((states) => {
      this.mapStates = states;
      this.cdr.markForCheck();
    });

    const featuredStream = this.isIframe
      ? this.mapStateService.findFeaturedForMapPublic(mapId)
      : this.mapStateService.findFeaturedForMap(mapId);
    featuredStream.subscribe((state) => {
      if (state.length === 0) {
        this.featuredState = null;
        return;
      } else {
        this.featuredState = first(state)!;
      }

      // auto-select featured state when appropriate
      // For viewers/commenters (not owner and no edit permission), auto-select like smaplets
      const currentMapFromStore = this.mapService.getCurrentSelectedMapFromStore();
      const isMapOwner = currentMapFromStore?.owner?.id === this.currentUser?.id;
      const canAccessBaseState = isMapOwner || this.hasMapRole('edit');
      if (
        autoSelect &&
          (this.isIframe || !canAccessBaseState)
      ) {
        this.selectMapState(this.featuredState || this.defaultState);
      }
      this.cdr.markForCheck();
    });
  }

  selectMapState(state: MapStateDto) {
    this.mapStateService.selectState(state);
    this.cdr.markForCheck();
  }

  async openGptDialog(event?: MouseEvent) {
    if (event) {
      event.stopPropagation();
    }
    if (!this.canUseAssistant()) {
      return;
    }

    if (!(await this.aiStateService.waitForAIFeaturesEnabled())) {
      this.showAiConfigureBubble();
      return;
    }

    const ref = this.matBottomSheet.open(ChatBottomsheetComponent, {
      panelClass: 'chat-bottomsheet',
      restoreFocus: false,
    });
    ref.afterDismissed().subscribe((result) => {
      if (result?.data) {
        const {action, data} = result;

        // Extract nodes and links from transformed preview format
        // Preview format has links embedded inside nodes, we need to extract them
        const rawNodes = data.nodes || [];

        // Extract links from inside nodes (preview format) or from top level
        let rawLinks = data.links || [];
        if (rawLinks.length === 0 && rawNodes.length > 0) {
          // Links are embedded in nodes - extract them
          rawLinks = rawNodes.flatMap((node: any) => node.links || []);
        }

        // Clean nodes - remove embedded links array and strip metadata IDs
        const cleanedNodes = rawNodes.map((node: any) => {
          const {links, ...nodeWithoutLinks} = node;
          return {
            ...nodeWithoutLinks,
            metadata: Array.isArray(node.metadata)
              ? node.metadata.map((m: any) => {
                const {id, ...rest} = m;
                return rest;
              })
              : []
          };
        });

        // Helper to extract node ID from various formats (handles nested objects)
        const extractId = (ref: any, depth = 0): string | null => {
          if (!ref) return null;
          if (typeof ref === 'string') return ref;
          if (depth > 3) return null;
          if (typeof ref === 'object' && ref.id) {
            return extractId(ref.id, depth + 1);
          }
          return null;
        };

        // Clean links - convert from/to objects back to IDs and strip metadata IDs
        const cleanedLinks = rawLinks.map((link: any) => {
          const fromId = extractId(link.from);
          const toId = extractId(link.to);
          return {
            ...link,
            from: fromId,
            to: toId,
            metadata: Array.isArray(link.metadata)
              ? link.metadata.map((m: any) => {
                const {id, ...rest} = m;
                return rest;
              })
              : []
          };
        }).filter((link: any) => link.from && link.to);

        if (action === 'insert') {
          // Insert into current map without reload
          const payload = {
            nodes: cleanedNodes as GraphNode[],
            links: cleanedLinks as GraphNodeLink[],
            mapId: this.currentMap?.id as string,
          };
          this.nodeService.createGenerated(payload).subscribe((res: any) => {
            const cy = this.cyService.cy;
            if (cy) {
              const parentNodes = (res.nodes || []).filter((n: any) => !n.parent);
              const childNodes = (res.nodes || []).filter((n: any) => !!n.parent);
              cy.batch(() => {
                for (const node of parentNodes) {
                  if (!cy.$('#' + node.id).length) {
                    cy.add({
                      group: 'nodes',
                      data: {...node, label: 'name'},
                      position: {x: node.x || 0, y: node.y || 0}
                    });
                  }
                }
                for (const node of childNodes) {
                  const parentId = typeof node.parent === 'object' ? node.parent?.id : node.parent;
                  if (!cy.$('#' + node.id).length) {
                    cy.add({
                      group: 'nodes',
                      data: {...node, label: 'name', parent: parentId},
                      position: {x: node.x || 0, y: node.y || 0}
                    });
                  }
                }
                (res.links || []).forEach((link: any) => {
                  const sourceId = typeof link.from === 'object' ? link.from?.id : link.from;
                  const targetId = typeof link.to === 'object' ? link.to?.id : link.to;
                  if (sourceId && targetId && !cy.$('#' + link.id).length) {
                    cy.add({
                      group: 'edges',
                      data: {...link, source: sourceId, target: targetId, label: 'name'}
                    });
                  }
                });
              });
              // Move existing nodes to new parents if reparented
              const parentUpdates: Record<string, string> = res.parentUpdates || {};
              for (const [nodeId, pId] of Object.entries(parentUpdates)) {
                const cyNode = cy.$('#' + nodeId);
                if (cyNode.length && cy.$('#' + pId).length) {
                  cyNode.move({parent: pId});
                }
              }
              cy.style().update();
              cy.elements().unselect();
              const newElements = cy.collection();
              (res.allIds || []).forEach((id: string) => {
                const el = cy.$('#' + id);
                if (el.length) {
                  el.select();
                  newElements.merge(el);
                }
              });
              if (newElements.length) {
                cy.animate({fit: {eles: newElements, padding: 80}}, {duration: 600});
              }
            }
          });
        } else if (action === 'createNew') {
          // Create a new map
          this.mapService.createMapFromAiData(
            cleanedNodes as GraphNode[],
            cleanedLinks as GraphNodeLink[]
          ).subscribe((res: any) => {
            this.router.navigate(['/dashboard/map', res.id]).then(() => {
              location.reload();
            });
          });
        }
      }
    });
  }

  showAiConfigureBubble() {
    this.showAiBubble = true;
    this.aiBubblesStateService.setSource(AIGenerationBubbleSource.toolbar);
  }

  onAiBubbleClose() {
    this.showAiBubble = false;
    this.aiBubblesStateService.clearSource();
  }

  private async initAll() {
    if (this.hasInitializedAll) return;
    this.hasInitializedAll = true;

    const isIframe = isInIframe();
    if (!isIframe) {
      await this.packageService.init();
      this.currentUser = this.userService.getCurrentUserFromStorage();
      this.initAllSubscriptions.add(
        this.mapService.openMapSheetSubject.pipe(takeUntil(this.destroy$)).subscribe(() => this.selectMap())
      );

      this.settingsService.getGeneralInfo().subscribe((info) => {
        this.buildNumber = {
          build: info.buildNumber,
          version: environment.version,
        };
        this.showVersionInfo =
          localStorage.getItem('buildNumber') === 'true' || false;
      });
      this.packageService.loadUserPackage().subscribe((res) => {
        this.servicePlan = res?.package.title || '';
      });
    }
    this.isSidebarOpen =
      this.settingsService.getLocalStorageSettings()?.isSidebarOpen || false;
    this.initAllSubscriptions.add(
      this.settingsService.localStorageSettingsChanged.pipe(takeUntil(this.destroy$)).subscribe((settings) => {
        if (settings.isSidebarOpen !== undefined) {
          this.isSidebarOpen = settings.isSidebarOpen;
        }
      })
    );
    this.initAllSubscriptions.add(
      CytoscapeService.menuChangedSubject.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.loadToursAndViews(false);
      })
    );
    // Re-sort the toolbar models menu whenever the legend changes (reorder,
    // include/exclude, etc.) so it stays aligned with the legend panel.
    this.initAllSubscriptions.add(
      this.legendService.currentLegendSubject.pipe(takeUntil(this.destroy$)).subscribe(() => {
        if (this.nodeModels.length > 0 || this.linkModels.length > 0) {
          this.applyLegendOrdering();
          this.rebuildModelIconCache();
        }
      })
    );
    this.settingsService.initLocalLinkViewMode();
    this.settingsService.initLocalOtherLinksMode();
    this.settingsService.initLocalOtherNodesMode();
    setTimeout(() => {
      //select ids from sessionstorage
      const idsToSelect = sessionStorage.getItem('selectAfterReload');
      if (idsToSelect) {
        const cy = this.cyService.cy;
        if (!cy) {
          return;
        }
        const ids = JSON.parse(idsToSelect);
        ids.forEach((id: string) => {
          cy?.getElementById(id).select();
        });
        this.cyService.centerCanvas();
        sessionStorage.removeItem('selectAfterReload');
      }
      this.loadToursAndViews(false);
    }, 1000);
  }

  cancelGptGeneratedMap() {
    location.reload();
  }

  openSettings() {
    this.dialog.open(SettingsDialogComponent, {
      width: '35%',
    });
  }

  openAccount() {
    this.dialog.open(UserAccountDialogComponent, {
      width: '50%',
      autoFocus: false,
    });
  }

  openAiUsage() {
    this.dialog.open(AiUsageDialogComponent, {
      width: '50%',
      autoFocus: false,
    });
  }

  openProfile() {
    this.dialog.open(UserProfileDialogComponent, {
      width: '35%',
      autoFocus: false,
    });
  }

  toggleSidebar() {
    this.settingsService.toggleSidebar();
    this.isSidebarOpen =
      this.settingsService.getLocalStorageSettings()?.isSidebarOpen || false;
  }

  shareMap() {
    const ref = this.dialog.open(ShareMapDialogComponent, {
      width: '55%',
      data: {
        map: this.currentMap,
      },
    });

    ref.afterClosed().subscribe((res) => {
      if (res) {
        // Update entire map, including sharedWith and userRoles, from server response
        this.currentMap = res;
        this.topToolbarStateService.setMapName(this.currentMap!.name);
        this.mapService.setCurrentSelectedMapToStore(res);

        // Force reload the map from server to ensure fresh userRoles data
        if (this.currentMap?.id) {
          this.mapService
            .findByIdWithRelation(this.currentMap.id)
            .subscribe((updatedMap) => {
              this.currentMap = updatedMap;
              this.topToolbarStateService.setMapName(this.currentMap.name);
              this.mapService.setCurrentSelectedMapToStore(updatedMap);
              // Trigger change detection to update menu states
              this.cdr.markForCheck();
            });
        }
      }
    });
  }

  transferMap() {
    if (!this.isOwner) {
      return;
    }

    this.dialog.open(TransferMapDialogComponent, {
      width: '35%',
      data: {
        // @ts-ignore
        map: this.currentMap,
      },
    });
  }

  embedMap() {
    const ref = this.dialog.open(EmbedDialogComponent, {
      width: '50%',
      autoFocus: false,
      data: {
        // @ts-ignore
        map: this.currentMap,
        mapName: this.currentMap?.name,
        isPublic: this.currentMap?.isPublic || false,
      },
    });
    ref.afterClosed().subscribe((res) => {
      if (res?.published) {
        this.currentMap!.isPublic = res.published;
        this.mapService.setCurrentSelectedMapToStore(this.currentMap!);
      } else if (res) {
        this.currentMap!.isPublic = false;
        this.mapService.setCurrentSelectedMapToStore(this.currentMap!);
      }
    });
  }

  createMapState() {
    const ref = this.dialog.open(CreateNewMapStateDialogComponent, {
      width: '35%',
    });

    ref.afterClosed().subscribe((state: MapStateDto) => {
      if (state) {
        this.selectMapState(state);
      }
    });
  }

  createMapStateFromCurrent() {
    const currentStateId = this.currentNotDefaultMapStateId;
    const ref = this.dialog.open(CreateNewMapStateDialogComponent, {
      width: '35%',
      data: {sourceStateId: currentStateId},
    });

    ref.afterClosed().subscribe((state: MapStateDto) => {
      if (state) {
        // The server just created state entries for all elements, but the frontend
        // cache (cytoscape node data + overlay manager) doesn't have them yet.
        // Populate the local caches before switching so elements stay in place.
        this.populateLocalStateEntries(state.id!, currentStateId);
        this.selectMapState(state);
      }
    });
  }

  private populateLocalStateEntries(newStateId: string, sourceStateId: string | null) {
    const cy = this.cyService.cy;
    if (cy) {
      // Nodes
      cy.nodes().forEach((node: any) => {
        if (node.data('excludedFromState')) return;
        const states = node.data('states') || [];

        let newNodeData: any;
        if (sourceStateId) {
          const sourceEntry = states.find((s: any) => s.stateId === sourceStateId);
          if (sourceEntry) {
            newNodeData = JSON.parse(JSON.stringify(sourceEntry.node));
          }
        }
        if (!newNodeData) {
          newNodeData = {
            id: node.id(),
            name: node.data('name'),
            imageUrl: node.data('imageUrl'),
            color: node.data('color'),
            type: node.data('type'),
            hasBorder: node.data('hasBorder') ?? false,
            collapsed: node.data('collapsed') ?? false,
            weight: node.data('weight') ?? 1,
            x: node.position().x,
            y: node.position().y,
            description: node.data('description'),
            properties: node.data('properties') ? JSON.parse(JSON.stringify(node.data('properties'))) : undefined,
          };
        }

        states.push({stateId: newStateId, node: newNodeData});
        node.data('states', states);
      });

      // Edges
      cy.edges().forEach((edge: any) => {
        if (edge.data('excludedFromState')) return;
        const states = edge.data('states') || [];

        let newLinkData: any;
        if (sourceStateId) {
          const sourceEntry = states.find((s: any) => s.stateId === sourceStateId);
          if (sourceEntry) {
            newLinkData = JSON.parse(JSON.stringify(sourceEntry.link));
          }
        }
        if (!newLinkData) {
          newLinkData = {
            id: edge.id(),
            name: edge.data('name'),
            color: edge.data('color'),
            weight: edge.data('weight') ?? 1,
            type: edge.data('type'),
            shape: edge.data('shape'),
            direction: edge.data('direction'),
            edgeArrowType: edge.data('edgeArrowType'),
            labelSlope: edge.data('labelSlope'),
            controlPointPositions: edge.data('controlPointPositions') || [],
            bendPointPositions: edge.data('bendPointPositions') || [],
            cyedgebendeditingDistances: edge.data('cyedgebendeditingDistances') || [],
            cyedgebendeditingWeights: edge.data('cyedgebendeditingWeights') || [],
            cyedgecontroleditingDistances: edge.data('cyedgecontroleditingDistances') || [],
            cyedgecontroleditingWeights: edge.data('cyedgecontroleditingWeights') || [],
          };
        }

        states.push({stateId: newStateId, link: newLinkData});
        edge.data('states', states);
      });
    }

    // Overlay elements
    this.overlayManager.populateStateEntries(newStateId, sourceStateId);
  }

  get hasExperimentalStateCreate(): boolean {
    return !!localStorage.getItem('EXPERIMENTAL_STATE_CREATE');
  }

  get currentNotDefaultMapState() {
    const currentMapState = this.mapStateService.getCurrentLocalState(this.currentMap?.id);
    if (currentMapState?.id === undefined) {
      return null;
    }
    return currentMapState?.name;
  }

  get currentNotDefaultMapStateId() {
    const currentMapState = this.mapStateService.getCurrentLocalState(this.currentMap?.id);
    if (currentMapState?.id === undefined) {
      return null;
    }
    return currentMapState?.id;
  }

  centerCanvas() {
    invalidateGeneralPanZoom();
    invalidatePanZoomForCollapsedNode();
    // Delete pan/zoom state for this map - home button should fill visible canvas
    deletePanZoomState();
    this.cyService.centerCanvas(true, false, false, true, true);
  }

  addRectangle() {
    const cy = this.cyService.cy;
    if (!cy) return;
    // Place rectangle at the center of the viewport
    const extent = cy.extent();
    const x = (extent.x1 + extent.x2) / 2;
    const y = (extent.y1 + extent.y2) / 2;
    // Access the dashboard component through the cytoscape container's angular context
    const dashboard = (window as any).__dashboardComponentRef;
    if (dashboard?.createOverlayElement) {
      dashboard.createOverlayElement(x, y);
    }
  }

  selectMap(map?: GraphMap, isNew = false) {
    const navigateToMap = (m: GraphMap) => {
      this.topToolbarStateService.setMapName(m!.name);
      this.router.navigate(['/dashboard', 'map', m?.id], {
        queryParams: {
          //@ts-ignore
          isNew: m?.isNew,
        },
      });
    };

    if (map) {
      navigateToMap(map);
    } else {
      const ref = this.matBottomSheet.open(MapListBottomsheetComponent, {panelClass: 'smaps-bottomsheet'});
      ref.afterDismissed().subscribe((selectedMap: MapFromList) => {
        if (selectedMap) {
          navigateToMap(selectedMap);
        }
      });
    }
  }

  openStateListSheet() {
    const ref = this.matBottomSheet.open(StateListBottomsheetComponent);

    ref.afterDismissed().subscribe(async (state: any) => {
      if (state) {
        this.selectMapState(state);
      }
    });
  }

  async selectTour(tour?: MapTour) {
    const canOpen = this.hasMapRole('edit') || this.hasMapRole('manage');
    if (this.currentMap?.isReadonly && !canOpen) {
      return;
    }
    if (tour) {
      await this.tourService.playTour(tour);
    } else {
      const ref = this.matBottomSheet.open(TourListBottomsheetComponent);
      ref.afterDismissed().subscribe(async (tour: any) => {
        if (tour) {
          await this.tourService.playTour(tour);
        }
        this.loadToursAndViews();
      });
    }
  }

  selectView(view?: SavedView) {
    if (!this.hasMapRole('edit') && !view) {
      return;
    }
    if (view) {
      this.viewService.playView(view);
    } else {
      const ref = this.matBottomSheet.open(ViewsListBottomsheetComponent);
      ref.afterDismissed().subscribe((view: any) => {
        if (view) {
          this.viewService.playView(view);
        }
        this.loadToursAndViews();
      });
    }
  }

  createFilter() {
    if (!this.canManageFilters || !this.hasMapRole('edit')) {
      return;
    }
    const ref = this.dialog.open(CreateFilterDialogComponent);
    ref.afterClosed().subscribe((filter: FilterDto) => {
      if (filter) {
        this.loadToursAndViews();
        CytoscapeService.menuChangedSubject.next();
      }
    });
  }

  selectFilter(filter?: FilterDto) {
    if (!filter) {
      const ref = this.matBottomSheet.open(FilterListBottomsheetComponent);
      ref.afterDismissed().subscribe((selectedFilter: FilterDto) => {
        if (selectedFilter) {
          this.filterService.toggleFilter(selectedFilter);
        }
        this.loadToursAndViews();
      });
    } else {
      this.filterService.toggleFilter(filter);
    }
  }

  isFilterActive(filterId: string): boolean {
    return this.filterService.isFilterActive(filterId);
  }

  createModel(fromBottomSheet = false) {
    if (!this.hasMapRole('edit')) {
      return;
    }

    // Get current selection from cytoscape
    const cy = this.cyService.cy;
    if (!cy) return;

    const selected = cy.$(':selected');
    if (selected.length === 0) {
      // Show warning dialog instead of error notification
      const warningRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '540px',
        maxWidth: '90vw',
        data: {
          title: this.translate.instant('NAV_MISC.APPLY_MODEL_TITLE'),
          icon: 'warning',
          message: this.translate.instant('NAV_MISC.CREATE_MODEL_NO_SELECTION'),
          confirmText: this.translate.instant('NAV_MISC.OK'),
          showCancel: false,
        },
      });

      // If called from bottom sheet, reopen it after dialog closes
      if (fromBottomSheet) {
        warningRef.afterClosed().subscribe(() => {
          this.manageModels();
        });
      }
      return;
    }
    if (selected.length > 1) {
      // Show warning dialog instead of error notification
      const warningRef = this.dialog.open(ConfirmationDialogComponent, {
        width: '540px',
        maxWidth: '90vw',
        data: {
          title: this.translate.instant('NAV_MISC.MULTIPLE_SELECTION_TITLE'),
          icon: 'warning',
          message: this.translate.instant('NAV_MISC.CREATE_MODEL_MULTIPLE'),
          confirmText: this.translate.instant('NAV_MISC.OK'),
          showCancel: false,
        },
      });

      // If called from bottom sheet, reopen it after dialog closes
      if (fromBottomSheet) {
        warningRef.afterClosed().subscribe(() => {
          this.manageModels();
        });
      }
      return;
    }

    const selectedElement = selected[0];
    const isNode = selectedElement.isNode();
    const elementType = isNode ? ModelElementType.NODE : ModelElementType.LINK;
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;

    if (!mapId) return;

    const dialogData = {
      selectedElement: selectedElement.data(),
      elementType,
      mapId,
    };

    const ref = this.dialog.open(CreateModelDialogComponent, {
      width: '50%',
      data: dialogData,
    });

    ref.afterClosed().subscribe((model: ModelDto) => {
      if (model) {
        this.loadToursAndViews();
        CytoscapeService.menuChangedSubject.next();
      }
    });
  }

  manageModels() {
    const ref = this.matBottomSheet.open(ModelListBottomsheetComponent);
    // No need to handle afterDismissed since create is handled within the bottom sheet
  }

  applyModel(model: ModelDto) {
    if (!this.hasMapRole('edit') && !this.isDefaulter) {
      return;
    }

    // Get current selection from cytoscape
    const cy = this.cyService.cy;
    if (!cy) return;

    const selected = cy.$(':selected');
    const relevantElements = selected.filter((ele: any) => {
      return model.elementType === ModelElementType.NODE
        ? ele.isNode()
        : ele.isEdge();
    });

    if (relevantElements.length === 0) {
      const expectedType =
        model.elementType === ModelElementType.NODE ? 'nodes' : 'links';
      this.notificationService.error(
        this.translate.instant('NAV_MISC.APPLY_MODEL_SELECT', {type: expectedType}),
      );
      return;
    }

    const elementCount = relevantElements.length;
    const elementType = model.elementType.toLowerCase();
    const pluralType = elementCount === 1 ? elementType : elementType + 's';

    // Check if user has disabled confirmation dialog
    const skipConfirmation =
      localStorage.getItem(SKIP_MODEL_APPLICATION_CONFIRMATION_KEY) === 'true';

    if (skipConfirmation) {
      // Apply model directly without confirmation
      this.performModelApplication(model, relevantElements.toArray());
      return;
    }

    // Show confirmation dialog
    const confirmationRef = this.dialog.open(ConfirmationDialogComponent, {
      width: '540px',
      maxWidth: '90vw',
      data: {
        title: this.translate.instant('NAV_MISC.APPLY_MODEL_TITLE'),
        icon: 'transform',
        message: this.translate.instant('NAV_MISC.APPLY_MODEL_CONFIRM', {
          name: model.name,
          count: elementCount,
          elements: pluralType,
        }),
        confirmText: this.translate.instant('NAV_MISC.APPLY'),
        cancelText: this.translate.instant('NAV_MISC.CANCEL'),
        showDoNotAskAgain: true,
      },
    });

    confirmationRef.afterClosed().subscribe((result: any) => {
      if (result?.confirmed) {
        // Save "do not ask again" preference
        if (result.doNotShowAgain) {
          localStorage.setItem(SKIP_MODEL_APPLICATION_CONFIRMATION_KEY, 'true');
        }
        this.performModelApplication(model, relevantElements.toArray());
      }
    });
  }

  private performModelApplication(model: ModelDto, relevantElements: any[]) {
    const elementIds = relevantElements.map((ele: any) => ele.id());
    const mapId = this.mapService.getCurrentSelectedMapFromStore()?.id;

    if (!mapId) return;

    const applyDto = {
      elementIds,
      mapId,
      inclusionOptions: model.inclusionOptions,
    };

    this.modelService.apply(model.id, applyDto).subscribe({
      next: (response) => {
        // Success - refresh the canvas silently (visual feedback is enough)
        if (response.updatedElements) {
          this.modelService.triggerElementsRefresh(response.updatedElements);
        }
      },
      error: (error) => {
        console.error('Error applying model:', error);
        this.notificationService.error(
          this.translate.instant('NAV_MISC.APPLY_MODEL_FAILED', {
            error: error.error?.message || error.message,
          }),
        );
      },
    });
  }

  sendMail() {
    window.open('mailto:support@maporium.com', '_blank');
  }

  visitWebsite() {
    window.open('https://www.maporium.com', '_blank');
  }

  private fixContextMenuTooltips() {
    // Comprehensive solution to strip browser tooltips and use custom ones
    const seen = new WeakMap();

    const stripTitle = (el: Element | null) => {
      if (!el || el.nodeType !== 1) return;
      if (seen.has(el)) return;

      const t = el.getAttribute('title');
      if (!t) return;

      // Keep info for custom tooltip + accessibility
      el.setAttribute('data-title', t);
      if (
        !el.hasAttribute('aria-label') &&
        !el.hasAttribute('aria-describedby')
      ) {
        el.setAttribute('aria-label', t);
      }
      el.removeAttribute('title');
      seen.set(el, t);
    };

    // 1) Initial sweep
    document.querySelectorAll('[title]').forEach(stripTitle);

    // 2) Intercept hover/focus early so the browser never shows the tooltip
    const handler = (e: Event) => {
      const el = (e.target as Element)?.closest('[title]');
      if (el) stripTitle(el);
    };
    document.addEventListener('mouseover', handler, { capture: true });
    document.addEventListener('focusin', handler, { capture: true });

    // 3) Watch for future DOM changes (new nodes or title changes)
    const obs = new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'title') {
          stripTitle(m.target as Element);
        } else if (m.type === 'childList') {
          m.addedNodes.forEach((n) => {
            if (n.nodeType !== 1) return;
            const node = n as Element;
            if (node.hasAttribute && node.hasAttribute('title'))
              stripTitle(node);
            node.querySelectorAll?.('[title]').forEach(stripTitle);
          });
        }
      }
    });

    obs.observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['title'],
    });
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    this.destroy$.next();
    this.destroy$.complete();
    this.initAllSubscriptions.unsubscribe();

    this.destroySmapLottie();
    this.destroyAssistantLottie();

    // Clean up transfer SSE connection
    this.mapService.cleanupTransferConnection();
  }

  private initSmapLottie(): void {
    this.destroySmapLottie();
    const container = document.querySelector(
      'maporium-toolbar .smap-toolbar-lottie'
    ) as HTMLElement | null;
    if (!container) return;
    this.smapLottieInstance = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: TopToolbarComponent.SMAP_LOTTIE_PATH,
    });
  }

  private destroySmapLottie(): void {
    this.smapLottieInstance?.destroy();
    this.smapLottieInstance = null;
  }

  private initAssistantLottie(): void {
    this.destroyAssistantLottie();
    const container = document.querySelector(
      'maporium-toolbar .assistant-toolbar-lottie'
    ) as HTMLElement | null;
    if (!container) return;
    this.assistantLottieInstance = lottie.loadAnimation({
      container,
      renderer: 'svg',
      loop: true,
      autoplay: true,
      path: TopToolbarComponent.ASSISTANT_LOTTIE_PATH,
    });
  }

  private destroyAssistantLottie(): void {
    this.assistantLottieInstance?.destroy();
    this.assistantLottieInstance = null;
  }

  editMap() {
    const map = this.mapService.selectedMap();
    if (!map) return;
    this.mapDialogsService.edit(map!).subscribe((m: GraphMap) => {
      if (m) {
        // this.bottomSheetRef.dismiss(map);
        this.maps = this.maps.map((m) => {
          if (m.id === m.id) {
            return m;
          }
          return m;
        });
        if (this.mapService.getCurrentSelectedMapFromStore()?.id === m.id) {
          this.mapService.mapNameChangedSubject.next(m.name);
          this.mapService.setCurrentSelectedMapToStore(m);
        }
      }
    });
  }

  async exportAsPng() {
    try {
      const blob = await this.screenshotService.captureMap(4); // 4x scale for high-quality export
      if (!blob) return;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${this.currentMap?.name || 'map'}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exporting PNG:', err);
    }
  }

  duplicateMap() {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap) return;

    this.mapDialogsService.duplicate(currentMap).subscribe((m: GraphMap) => {
      if (m) {
        this.tourService.activateTourSubject.next(null);
        this.selectMap(m);
      }
    });
  }

  deleteMapConfirm() {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap) return;
    this.mapDialogsService.deleteConfirmation(currentMap).subscribe((res) => {
      if (res) {
        this.deleteMap();
      }
    });
  }

  deleteMap() {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap) return;
    this.mapService.delete(currentMap.id).subscribe(() => {
      const mapId = currentMap.id;
      const storedMap = this.mapService.getCurrentSelectedMapFromStore();

      if (storedMap && storedMap.id === mapId) {
        this.mapService.removeCurrentSelectedMapFromStore();
      }
      const mapsWithoutDeleted = this.maps.filter((m) => m.id !== mapId);
      this.maps = mapsWithoutDeleted;
      const firstMap = mapsWithoutDeleted[0];
      if (firstMap) {
        this.router
          .navigate(['/dashboard', 'map', firstMap.id])
          .then(() => location.reload());
      } else {
        this.router.navigate(['/dashboard']).then(() => location.reload());
      }
    });
  }

  removeMapFromSharedWithMe() {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap) return;

    // Sharing takes precedence over library - if shared, always unshare
    // Only treat as store map if it's in library AND not shared with user
    const isShared = this.isSharedWithMe(currentMap);
    const isStoreMap = !isShared && this.isInLibrary(currentMap);

    this.mapDialogsService
      .deleteConfirmation(currentMap, true, isStoreMap)
      .subscribe((res) => {
        if (res) {
          const mapId = currentMap.id;

          // Remove from store library or unshare depending on map type
          const removeOperation = isStoreMap
            ? this.storeService.removeFromLibrary(mapId)
            : this.mapService.unshareMap(mapId);

          removeOperation.subscribe(() => {
            const storedMap = this.mapService.getCurrentSelectedMapFromStore();

            if (storedMap && storedMap.id === mapId) {
              this.mapService.removeCurrentSelectedMapFromStore();
            }

            const mapsWithoutDeleted = this.maps.filter((m) => m.id !== mapId);
            this.maps = mapsWithoutDeleted;
            const firstMap = mapsWithoutDeleted[0];
            if (firstMap) {
              this.router
                .navigate(['/dashboard', 'map', firstMap.id])
                .then(() => location.reload());
            } else {
              this.router
                .navigate(['/dashboard'])
                .then(() => location.reload());
            }
          });
        }
      });
  }

  exportMap() {
    const currentMap = this.mapService.getCurrentSelectedMapFromStore();
    if (!currentMap) return;
    this.mapDialogsService.download(currentMap);
  }

  private shouldListenToTransferUpdates(): boolean {
    if (isEmpty(this.currentMap)) {
      return false;
    }

    const currentUserId = this.userService.getCurrentUserFromStorage()?.id;
    const isOwner = this.currentMap?.owner?.id === currentUserId;

    // Only listen if:
    // 1. User is NOT the owner (they could potentially receive ownership)
    // 2. Map has other users (sharing is possible)
    const hasOtherUsers =
      (this.currentMap?.userRoles?.length || 0) > 0 ||
      (this.currentMap?.sharedWith?.length || 0) > 0;

    return !isOwner && hasOtherUsers;
  }

  listenToTransferEvent() {
    // Only connect if the current user could potentially receive a transfer
    if (!this.shouldListenToTransferUpdates()) {
      return;
    }

    this.mapService.connectToTransferUpdates(
      this.currentMap?.id as string,
      (data: GraphMap) => {
        // If the map is the current map from storage and we are the owner , then update the isReadonly property
        const currentMapFromStore =
          this.mapService.getCurrentSelectedMapFromStore();
        const isOwner =
          this.userService.getCurrentUserFromStorage().id === data.owner.id;
        if (currentMapFromStore?.id === data.id && isOwner) {
          currentMapFromStore.isReadonly = false;
          this.mapService.setCurrentSelectedMapToStore(currentMapFromStore);
        }
        location.reload();
      },
      (error: any) => {
        //ignore error
      },
    );
  }

  private loadMapForUser(mapId?: string, sharedMapId?: string) {
    this.mapService
      .findAllForUser(this.userService.getCurrentUserFromStorage().id)
      .pipe(
        catchError((err) => {
          this.isLoading = false;
          // If the API call fails (likely due to expired tokens), hide the toolbar
          if (err.status === 401) {
            this.isLoggedIn = false;
          }
          return throwError(err);
        }),
      )
      .subscribe((maps) => {
        this.isLoggedIn = true;
        // @ts-ignore
        this.maps = maps;
        const storedMap = readSelectedMap();
        const hasSelectedMapsInStorage =
          storedMap !== null &&
          storedMap !== undefined &&
          storedMap !== 'undefined';
        if (mapId || sharedMapId) {
          const map = this.maps.find((m) => m.id === (mapId || sharedMapId));
          if (map) {
            this.currentMap = map;
            this.topToolbarStateService.setMapName(this.currentMap.name);
            this.mapEditForm.setValue({ name: map.name });
            this.mapService.mapChangedSubject.next(map);
            this.mapService.setCurrentSelectedMapToStore(map);
            // Check if already at correct URL - skip navigation if so
            const currentUrlMapId = this.router.url.match(/\/dashboard\/map\/([^/?]+)/)?.[1];
            if (currentUrlMapId === this.currentMap.id) {
              // Already at correct URL, just initialize
              setTimeout(() => this.isLoading = false, 1000);
              localStorage.removeItem('sharedMapId');
              this.initAll();
              this.listenToTransferEvent();
              return;
            }
            this.router
              .navigate(['/dashboard', 'map', this.currentMap.id], {
                relativeTo: this.route,
                queryParamsHandling: 'preserve',
              })
              .then(() => {
                setTimeout(() => this.isLoading = false, 1000);
                localStorage.removeItem('sharedMapId');
                this.initAll();
                this.listenToTransferEvent();
                this.mapEditForm.setValue({ name: map.name });
              });
            return; // Don't fall through to navigation at end
          }
        }

        if (
          this.maps.length > 0 &&
          !hasSelectedMapsInStorage &&
          !mapId &&
          !sharedMapId
        ) {
          this.currentMap = this.maps[0];
          this.topToolbarStateService.setMapName(this.currentMap.name);
          this.mapService.mapChangedSubject.next(this.currentMap);
          this.mapService.setCurrentSelectedMapToStore(this.currentMap);
          this.mapEditForm.setValue({ name: this.currentMap.name });
        }

        if (hasSelectedMapsInStorage && !mapId && !sharedMapId) {
          // First check if currentMapFrom storage is in the list of maps
          const map = JSON.parse(storedMap || '{}');
          const mapInList = this.maps.find((m) => m.id === map.id);
          if (mapInList) {
            this.currentMap = mapInList;
            this.topToolbarStateService.setMapName(this.currentMap.name);
            this.mapService.mapChangedSubject.next(mapInList);
            this.mapService.setCurrentSelectedMapToStore(mapInList);
            this.mapEditForm.setValue({ name: mapInList.name });
          } else {
            this.currentMap = this.maps[0];
            this.mapService.mapChangedSubject.next(this.currentMap);
            this.mapService.setCurrentSelectedMapToStore(this.currentMap);
            this.mapEditForm.setValue({ name: this.currentMap.name });
            this.topToolbarStateService.setMapName(this.currentMap.name);
          }
        }

        // A document draft is a page of its own: load the maps but stay on it.
        if (this.router.url.startsWith('/dashboard/draft/')) {
          setTimeout(() => (this.isLoading = false), 1000);
          localStorage.removeItem('sharedMapId');
          this.initAll();
          this.listenToTransferEvent();
          return;
        }

        // Only navigate to map if currentMap is set, otherwise navigate to dashboard
        if (this.currentMap?.id) {
          this.router
            .navigate(['dashboard/map', this.currentMap.id], {
              relativeTo: this.route,
              queryParamsHandling: 'preserve',
            })
            .then(() => {
              setTimeout(() => {
                this.isLoading = false;
              }, 1000);
              localStorage.removeItem('sharedMapId');
              this.initAll();
              this.listenToTransferEvent();
              this.mapEditForm.setValue({ name: this.currentMap!.name });
            });
        } else {
          // No maps — already on /dashboard, just initialize
          setTimeout(() => this.isLoading = false, 1000);
          localStorage.removeItem('sharedMapId');
          this.initAll();
        }
      });
  }

  logout() {
    this.userService.logout();
    this.router.navigate(['/signin']);
  }

  async importSmapArchive() {
    if (!(await this.leaveDraft())) return;
    this.dialog.open(ImportSmapDialogComponent, {
      width: '35%',
      hasBackdrop: false,
    });
  }

  importPdfDocument() {
    this.dialog.open(ImportMapDialogComponent, {
      width: '35%',
      hasBackdrop: false,
      data: { importType: 'pdf' } as ImportDialogData,
    });
  }

  generateSmapFromPrompt() {
    this.dialog.open(GenerateSmapPromptDialogComponent, {
      width: '35%',
      hasBackdrop: false,
    });
  }

  openAiOptimizerDialog(): void {
    if (!this.currentMap) {
      return;
    }
    this.dialog.open(AiOptimizerDialogComponent, {
      width: '600px',
      data: {
        map: this.currentMap,
        canEdit: this.canEdit(),
      } as AiOptimizerDialogData,
    });
  }

  openStoreBottomsheet() {
    const ref = this.matBottomSheet.open(StoreBrowserBottomsheetComponent, {
      panelClass: 'store-browser-bottomsheet',
      restoreFocus: false,
    });
    ref.afterDismissed().subscribe((map: GraphMap) => {
      if (map) {
        this.tourService.activateTourSubject.next(null);
        this.router.navigate(['/dashboard', 'map', map.id]);
      }
    });
  }

  openPublishDialog() {
    if (!this.currentMap) return;

    this.dialog.open(PublishToStoreDialogComponent, {
      width: '400px',
      data: {
        map: this.currentMap
      }
    });
  }

  get canPublishToStore() {
    return (
      this.packageService.hasPackage(PACKAGE_NAMES.publisher) ||
      this.packageService.hasPackage(PACKAGE_NAMES.analyst) ||
      this.packageService.hasPackage(PACKAGE_NAMES.developer)
    );
  }
}

interface MapFromList extends GraphMap {
  isNew?: boolean;
  isDeleted?: boolean;
}
